import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useContext} from 'react';

import {assert} from 'Asserts';
import {getChatFileDownloadUrl, getChatFilePreviewUrl} from 'llm/chat-library/ChatDAO';
import {AnalysisWorkspaceContext} from 'llm/chat-library/layouts/analysis-workspace/AnalysisWorkspaceContext';
import {getActiveShareLinkItemId} from 'ShareAccess';
import Chip, {ChipSize} from 'ui-library/chips/Chip';
import Tooltip from 'ui-library/floating-ui/Tooltip';

/**
 * Determine if the currently-loaded chat is visible only due to share link access.
 *
 * Share link chat sessions don't allow file downloads, so we need to detect this
 * case. When opening a share link item and the user already has full access, they
 * will be redirected to the non-share-link mode, so we won't have an active share
 * link in that case.
 */
function isOpenedViaShareLink(chatSessionId: string | null): boolean {
  if (chatSessionId == null) {
    return false;
  }
  const shareLinkItemId = getActiveShareLinkItemId();
  return shareLinkItemId != null && shareLinkItemId === chatSessionId;
}

export interface ChatUploadedFileChipProps {
  chatSessionId: string | null;
  externalFileId: string | null;
  filename: string;
  mediaType: string;
}

/**
 * Chip component for displaying and re-downloading an uploaded file in a chat message.
 *
 * When this chat is opened via a share link, the user won't have access to download the
 * file, so just show a chip with the filename. We could consider revisiting this behavior
 * in the future, but disabling share link downloads is more defensive, consistent with
 * other products, and avoids some technical hassle around the share link propagating to
 * the new browser tab.
 */
export default function ChatUploadedFileChip({
  chatSessionId,
  externalFileId,
  filename,
  mediaType,
}: ChatUploadedFileChipProps): JSX.Element {
  const workspaceContext = useContext(AnalysisWorkspaceContext);
  const isChipClickable = externalFileId != null;
  const tooltipText = workspaceContext != null ? 'Click to preview file' : 'Click to download file';

  const handleFileClick = useCallback(() => {
    assert(externalFileId != null, 'Unexpected null external file ID on file download');
    assert(chatSessionId != null, 'Unexpected null chat session ID on file download');
    if (workspaceContext != null) {
      workspaceContext.openFile(chatSessionId, externalFileId, filename, mediaType);
    } else {
      window.open(getChatFileDownloadUrl(chatSessionId, externalFileId));
    }
  }, [chatSessionId, externalFileId, filename, mediaType, workspaceContext]);

  if (chatSessionId == null || isOpenedViaShareLink(chatSessionId)) {
    return <Chip text={filename} icon="paperclip" size={ChipSize.SMALL} />;
  } else if (externalFileId != null && (mediaType === 'image/png' || mediaType === 'image/jpeg')) {
    return (
      <Tooltip text={tooltipText}>
        <img
          className={css(styles.image)}
          src={getChatFilePreviewUrl(chatSessionId, externalFileId)}
          onClick={handleFileClick}
        />
      </Tooltip>
    );
  } else {
    return (
      <Chip
        text={filename}
        icon="paperclip"
        size={ChipSize.SMALL}
        onClick={isChipClickable ? handleFileClick : undefined}
        tooltipTitle={isChipClickable ? tooltipText : undefined}
      />
    );
  }
}

const styles = StyleSheet.create({
  image: {
    cursor: 'pointer',
  },
});
